import { ComponentType, ReactNode, Suspense, useMemo } from 'react';
import { Role, RoleType } from '@blisspointmedia/bpm-types/dist/Users';
import { NotFound } from '@Pages/NotFound';
import { useAppSelector } from '../redux';
import * as UserRedux from '../redux/user';
import { Page } from './Page';
import { FullPageSpinner } from './Spinner';

interface ProtectedPageProps {
  component: ComponentType<any>;
  suspense?: boolean;
  roleRequired?: RoleType | RoleType[];
  [key: string]: any;
}

export const ProtectedPage = ({
  component: Comp,
  suspense = false,
  roleRequired,
  ...restProps
}: ProtectedPageProps): ReactNode => {
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const userFetched = useAppSelector(UserRedux.userFetchedSelector);
  const userRole = useAppSelector(UserRedux.roleSelector);

  // Allow each protected route to specify the role of the user they allow
  // and default to isInternal if not specified
  const userNotAllowed = useMemo(() => {
    if (roleRequired && Array.isArray(roleRequired)) {
      const allowedRoles: number[] = [Role.ADMIN, ...roleRequired];
      return !allowedRoles.includes(userRole);
    }

    if (roleRequired) {
      return roleRequired < userRole;
    }

    return !isInternal;
  }, [isInternal, roleRequired, userRole]);

  if (!userFetched) {
    return (
      <div className='bpmPage'>
        <FullPageSpinner />
      </div>
    );
  }

  if (userNotAllowed) {
    return <NotFound />;
  }

  if (suspense) {
    return (
      <Suspense
        fallback={
          <Page title='' pageType=''>
            <FullPageSpinner />
          </Page>
        }
      >
        <Comp {...restProps} />
      </Suspense>
    );
  }

  return <Comp {...restProps} />;
};
